import {
  Card,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Skeleton,
  Typography,
} from "@mui/material";
import WDBox from "../../../../MaterialComponents/WDBox";
import WDButton from "../../../../MaterialComponents/WDButton";
import WDTypography from "../../../../MaterialComponents/WDTypography";

import { Formik, Form, Field } from "formik";
import { object, string } from "yup";
import { useEffect, useRef, useState } from "react";
import AppService from "../../../../Configurations/Appservice";
import { useDispatch } from "react-redux";
import {
  dismissLoader,
  showLoader,
  showSuccessMessage,
  updateScreenTitle,
} from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import { useLocation, useNavigate } from "react-router-dom";
import SubTitle from "../../../../Components/SubTitle";

const initialValues = {
  organisationId: "",
  serviceId: "",
  serviceChargeIncTax: "",
  serviceCharge: "",
  govtCharge: "",
  taxOnServiceCharge: "",
};

const validationSchema = object({
  organisationId: string().required("Name is Mandatory"),
  serviceId: string().required("Phone Number is Mandatory"),
  govtCharge: string().required("Mandatory"),
  serviceChargeIncTax: string().required("Mandatory"),
});

const CustomerServiceMapping = () => {
  const [services, setService] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [isMappingLoading, mappingLoading] = useState(false);
  const [mapping, setMapping] = useState();
  const [isEditing, editing] = useState(false);

  const calculateServiceAndTaxFromInclusive = (inclusiveAmount) => {
    const inc = parseFloat(inclusiveAmount);
    if (!Number.isFinite(inc)) {
      return { serviceCharge: "", taxOnServiceCharge: "" };
    }

    const rawService = inc / 1.05;
    const serviceCharge = Math.floor(rawService * 100) / 100;
    const tax = inc - serviceCharge;
    const taxOnServiceCharge = Math.round(tax * 100) / 100;

    return {
      serviceCharge: serviceCharge.toFixed(2),
      taxOnServiceCharge: taxOnServiceCharge.toFixed(2),
    };
  };

  const calculateInclusiveFromServiceAndTax = (serviceCharge, taxOnServiceCharge) => {
    const service = parseFloat(serviceCharge);
    const tax = parseFloat(taxOnServiceCharge);
    if (!Number.isFinite(service) || !Number.isFinite(tax)) return "";
    return (service + tax).toFixed(2);
  };

  const formikRef = useRef();
  const dispatch = useDispatch();
  const location = useLocation();
  const naviagte = useNavigate();

  useEffect(() => {
    dispatch(updateScreenTitle("Customer Organisation Charge Mapping"));
    return () => dispatch(updateScreenTitle(""));
  }, []);

  useEffect(() => {
    const item = location.state?.item;
    if (!item) {
      return;
    }
    
    // Set editing mode if we're not coming from workflow assignment
    if (!location.state?.fromWorkflowAssignment) {
      editing(true);
    }
    
    dispatch(showLoader());
    const organisationUrl = "v1/organisation/keyValues?type=CUSTOMER";
    const serviceUrl = "v1/service/keyValues";
    const maapedValueUrl = `v1/serviceMapping?serviceId=${item.serviceId}&organisationId=${item.organisationId}`;
    let endpoints = [organisationUrl, serviceUrl, maapedValueUrl];
    
    Promise.all(endpoints.map((endpoint) => AppService.get(endpoint)))
      .then(([organisations, services, mapping]) => {
        setOrganisations(organisations);
        setService(services);
        
        // Set default values for a new mapping from workflow assignment
        if (location.state?.fromWorkflowAssignment && (!mapping || !mapping.existing)) {
          setMapping({
            govtCharge: "0",
            taxOnServiceCharge: "5",
            serviceCharge: "50",
            existing: false
          });
          
          formikRef.current.setFieldValue("govtCharge", "0");
          formikRef.current.setFieldValue("taxOnServiceCharge", "5");
          formikRef.current.setFieldValue("serviceCharge", "50");
          formikRef.current.setFieldValue("serviceChargeIncTax", calculateInclusiveFromServiceAndTax("50", "5"));
        } else {
          // Use existing mapping data
          setMapping(mapping);
          formikRef.current.setFieldValue("govtCharge", mapping.govtCharge);
          formikRef.current.setFieldValue("taxOnServiceCharge", mapping.taxOnServiceCharge);
          formikRef.current.setFieldValue("serviceCharge", mapping.serviceCharge);
          formikRef.current.setFieldValue(
            "serviceChargeIncTax",
            calculateInclusiveFromServiceAndTax(mapping.serviceCharge, mapping.taxOnServiceCharge)
          );
        }
        
        // Always set the organization and service IDs
        formikRef.current.setFieldValue("serviceId", item.serviceId);
        formikRef.current.setFieldValue("organisationId", item.organisationId);
      })
      .catch((error) => {
        console.error("Error loading service mapping data:", error);
        
        // Set default values even on error for workflow assignment
        if (location.state?.fromWorkflowAssignment) {
          formikRef.current.setFieldValue("govtCharge", "0");
          formikRef.current.setFieldValue("taxOnServiceCharge", "5");
          formikRef.current.setFieldValue("serviceCharge", "50");
          formikRef.current.setFieldValue("serviceChargeIncTax", calculateInclusiveFromServiceAndTax("50", "5"));
          formikRef.current.setFieldValue("serviceId", item.serviceId);
          formikRef.current.setFieldValue("organisationId", item.organisationId);
        }
      })
      .finally(() => {
        dispatch(dismissLoader());
      });
  }, []);

  useEffect(() => {
    const item = location.state?.item;
    if (item) {
      return;
    }
    AppService.get("v1/service/keyValues")
      .then((response) => {
        setService(response);
      })
      .catch((error) => {});
  }, []);

  useEffect(() => {
    const item = location.state?.item;
    if (item) {
      return;
    }
    const params = { type: "CUSTOMER" };
    AppService.get("v1/organisation/keyValues", { params })
      .then((response) => {
        setOrganisations(response);
      })
      .catch((error) => {});
  }, []);

  const _submitHandler = (values, { resetForm }) => {
    console.log(values);
    dispatch(showLoader());
    const { serviceChargeIncTax, ...payload } = values;
    AppService.post("v1/serviceMapping", payload)
      .then((response) => {
        dispatch(showSuccessMessage("Service mapping successfully created"));
        
        // Check if we came from workflow assignment
        const returnData = localStorage.getItem('workflow_assignment_return');
        if (location.state?.fromWorkflowAssignment && returnData) {
          const returnInfo = JSON.parse(returnData);
          
          // Navigate back to workflow assignment
          naviagte(returnInfo.path);
          
          // Remove the return data from localStorage
          localStorage.removeItem('workflow_assignment_return');
        } else {
          // Regular flow - reset form
          resetForm();
        }
      })
      .catch((error) => {
        console.error("Error creating service mapping:", error);
      })
      .finally(() => {
        dispatch(dismissLoader());
      });
  };

  const _onServiceChange = (event, setFieldValue) => {
    mappingLoading(true);
    setFieldValue("serviceId", event.target.value);

    const params = {
      serviceId: event.target.value,
      organisationId: formikRef.current.values.organisationId,
    };
    AppService.get("v1/serviceMapping", { params })
      .then((response) => {
        setMapping(response);
        setFieldValue("govtCharge", response.govtCharge);
        setFieldValue("taxOnServiceCharge", response.taxOnServiceCharge);
        setFieldValue("serviceCharge", response.serviceCharge);
        setFieldValue(
          "serviceChargeIncTax",
          calculateInclusiveFromServiceAndTax(response.serviceCharge, response.taxOnServiceCharge)
        );
      })
      .catch((error) => {})
      .finally(() => {
        mappingLoading(false);
      });
  };

  const _renderChargesAreaLoader = () => {
    return (
      <WDBox mt={3}>
        <WDTypography fontSize={14}>Charge Mapping</WDTypography>
        <Grid container spacing={3} py={2}>
          <Grid item xs={12} md={3}>
            <WDBox>
              <Skeleton
                variant="rounded"
                height={40}
                animation={isMappingLoading ? "pulse" : false}
              />
            </WDBox>
          </Grid>
          <Grid item xs={12} md={3}>
            <WDBox>
              <Skeleton
                variant="rounded"
                height={40}
                animation={isMappingLoading ? "pulse" : false}
              />
            </WDBox>
          </Grid>
          <Grid item xs={12} md={3}>
            <WDBox>
              <Skeleton
                variant="rounded"
                height={40}
                animation={isMappingLoading ? "pulse" : false}
              />
            </WDBox>
          </Grid>
        </Grid>
      </WDBox>
    );
  };

  const _renderChargesArea = (errors, isValid, touched, dirty, setFieldValue) => {
    return (
      <WDBox mt={3}>
        <SubTitle>Charge Mapping</SubTitle>
        <Grid container spacing={3} py={3}>
          <Grid item xs={12} md={3}>
            <WDBox>
              <Field
                name="govtCharge"
                label="Government Charge"
                fullWidth
                as={TextField}
                error={Boolean(errors.govtCharge) && Boolean(touched.govtCharge)}
                helperText={Boolean(touched.govtCharge) && errors.govtCharge}
              />
            </WDBox>
          </Grid>
          <Grid item xs={12} md={3}>
            <WDBox>
              <Field
                name="serviceChargeIncTax"
                label="Service Charge"
                fullWidth
                as={TextField}
                onChange={(e) => {
                  const value = e.target.value;
                  setFieldValue("serviceChargeIncTax", value);
                  const { serviceCharge, taxOnServiceCharge } = calculateServiceAndTaxFromInclusive(value);
                  setFieldValue("serviceCharge", serviceCharge);
                  setFieldValue("taxOnServiceCharge", taxOnServiceCharge);
                }}
                error={Boolean(errors.serviceChargeIncTax) && Boolean(touched.serviceChargeIncTax)}
                helperText={
                  (Boolean(touched.serviceChargeIncTax) && errors.serviceChargeIncTax) ||
                  "Including tax"
                }
              />
            </WDBox>
          </Grid>
          <Grid item xs={12} md={3}>
            <WDBox>
              <Field
                name="taxOnServiceCharge"
                label="Tax On Service Charge"
                fullWidth
                as={TextField}
                disabled
                error={Boolean(errors.taxOnServiceCharge) && Boolean(touched.taxOnServiceCharge)}
                helperText={Boolean(touched.taxOnServiceCharge) && errors.taxOnServiceCharge}
              />
            </WDBox>
          </Grid>
          <Grid item xs={12} md={3}>
            <WDBox>
              <Field
                name="serviceCharge"
                label="Service Charge (Excl. Tax)"
                fullWidth
                as={TextField}
                disabled
                error={Boolean(errors.serviceCharge) && Boolean(touched.serviceCharge)}
                helperText={Boolean(touched.serviceCharge) && errors.serviceCharge}
              />
            </WDBox>
          </Grid>
        </Grid>
      </WDBox>
    );
  };

  return (
    <WDBox>
      <Card sx={{ padding: 3 }}>
        <Formik
          initialValues={initialValues}
          onSubmit={_submitHandler}
          validationSchema={validationSchema}
          innerRef={formikRef}
        >
          {({ values, errors, isValid, touched, dirty, setFieldValue }) => (
            <Form>
              <WDBox>
                <SubTitle fontSize={14}>Customer Service Mapping</SubTitle>
              </WDBox>
              <WDBox my={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <WDBox>
                      <FormControl fullWidth>
                        <InputLabel id="service-label">Customer Orgnisation</InputLabel>
                        <Field
                          name="organisationId"
                          label="Customer Orgnisation"
                          fullWidth
                          as={Select}
                          error={Boolean(errors.organisationId) && Boolean(touched.organisationId)}
                          helperText={Boolean(touched.organisationId) && errors.organisationId}
                          disabled={isEditing}
                        >
                          {organisations.map((item) => (
                            <MenuItem value={item.id}>{item.name}</MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                    </WDBox>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <WDBox>
                      <FormControl fullWidth>
                        <InputLabel id="service-label">Service</InputLabel>
                        <Field
                          name="serviceId"
                          label="Service"
                          fullWidth
                          as={Select}
                          disabled={!values.organisationId || isEditing}
                          onChange={(e) => _onServiceChange(e, setFieldValue)}
                          error={Boolean(errors.serviceId) && Boolean(touched.serviceId)}
                          helperText={Boolean(touched.serviceId) && errors.serviceId}
                          isLoading={true}
                        >
                          {services.map((item) => (
                            <MenuItem value={item.id}>{item.name}</MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                    </WDBox>
                  </Grid>
                </Grid>
                {!mapping || isMappingLoading
                  ? _renderChargesAreaLoader()
                  : _renderChargesArea(errors, isValid, touched, dirty, setFieldValue)}
              </WDBox>
              <WDBox display="flex" justifyContent="flex-end" pt={3}>
                <WDButton variant="gradient" disabled={!dirty || !isValid} type="submit">
                  {mapping?.existing ? "UPDATE" : "SUBMIT"}
                </WDButton>
              </WDBox>
            </Form>
          )}
        </Formik>
      </Card>
    </WDBox>
  );
};

export default CustomerServiceMapping;
